import React, { useState, useEffect, useRef } from 'react';
import { adminService } from '@/services/admin-service';
import { authService } from '@/services/auth-service';
import { AppHeader } from '@/components/AppHeader';
import { InlineList, InlineListRow } from '@/components/InlineList';
import { Modal } from '@/components/Modal';
import { ClassModuleAssignmentModal } from '@/components/ClassModuleAssignmentModal';
import type { AdminClassResponse, AdminModulesResponse, ClassModuleAssignment, ClassTimeWindow } from '@/types/admin';
import type { User } from '@/types/auth';
import { BookOpen, CalendarDays, ChevronDown, ChevronUp, Pencil, Plus, Search, Trash2, Users, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import './AdminModules.css';
import '../styles/common.css';

type AssignmentStatus = 'current' | 'future' | 'past';

const getAssignmentStatus = (assignment: ClassModuleAssignment): AssignmentStatus => {
  const now = new Date();
  const start = assignment.startDate ? new Date(assignment.startDate) : null;
  const end = assignment.endDate ? new Date(assignment.endDate) : null;

  if (start && start > now) return 'future';
  if (end && end < now) return 'past';
  return 'current';
};

const statusLabels: Record<AssignmentStatus, string> = {
  current: 'Current',
  future: 'Future',
  past: 'Past',
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = date.getDate().toString().padStart(2, '0');
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  return `${month} ${day}, ${year}`;
};

interface ModuleClassesListProps {
  module: AdminModulesResponse;
  moduleClasses: AdminClassResponse[];
  processingClassId: string | null;
  availableClasses: AdminClassResponse[];
  searchingClasses: boolean;
  classSearch: string;
  classSearchOpen: boolean;
  onClassSearchChange: (search: string) => void;
  onClassSearchFocus: () => void;
  onAddExistingClass: (moduleClass: AdminClassResponse) => void;
  onEditClass: (moduleClass: AdminClassResponse) => void;
  onRemoveClass: (moduleClass: AdminClassResponse) => void;
  onViewDays: (moduleId: string, moduleName: string, moduleClass: AdminClassResponse) => void;
  onViewUsers: (moduleClass: AdminClassResponse) => void;
}

const ModuleClassesList: React.FC<ModuleClassesListProps> = ({
  module,
  moduleClasses,
  processingClassId,
  availableClasses,
  searchingClasses,
  classSearch,
  classSearchOpen,
  onClassSearchChange,
  onClassSearchFocus,
  onAddExistingClass,
  onEditClass,
  onRemoveClass,
  onViewDays,
  onViewUsers,
}) => {
  const grouped = moduleClasses.reduce((acc, moduleClass) => {
    const assignment = moduleClass.moduleAssignments.find(a => a.moduleId === module.id);
    const status = assignment ? getAssignmentStatus(assignment) : 'current';
    acc[status].push(moduleClass);
    return acc;
  }, { current: [] as AdminClassResponse[], future: [] as AdminClassResponse[], past: [] as AdminClassResponse[] });

  const renderGroup = (status: AssignmentStatus, items: AdminClassResponse[]) => {
    if (status !== 'current' && items.length === 0) return null;

    return (
      <section className="module-class-group" key={status}>
        <h4 className={`module-class-group-title module-class-group-${status}`}>{statusLabels[status]}</h4>
        {items.length > 0 && (
          <InlineList className="module-class-list" compact>
          {items.map(moduleClass => {
            const assignment = moduleClass.moduleAssignments.find(a => a.moduleId === module.id);
            return (
              <InlineListRow
                actions={(
                  <>
                    <button
                      aria-label={`View users of ${moduleClass.name}`}
                      className="class-action"
                      data-tooltip="Users"
                      onClick={() => onViewUsers(moduleClass)}
                      type="button"
                    >
                      <Users aria-hidden="true" size={20} />
                    </button>
                    <button
                      aria-label={`View days of ${moduleClass.name}`}
                      className="class-action"
                      data-tooltip="Days"
                      onClick={() => onViewDays(module.id, module.name, moduleClass)}
                      type="button"
                    >
                      <CalendarDays aria-hidden="true" size={20} />
                    </button>
                    <span aria-hidden="true" className="class-action-separator" />
                    <button
                      aria-label={`Edit assignment ${moduleClass.name}`}
                      className="class-action"
                      data-tooltip="Edit"
                      onClick={() => onEditClass(moduleClass)}
                      type="button"
                    >
                      <Pencil aria-hidden="true" size={16} />
                    </button>
                    <button
                      aria-label={`Remove ${moduleClass.name} from this module`}
                      className="class-action"
                      data-tooltip="Remove"
                      disabled={processingClassId === moduleClass.id}
                      onClick={() => onRemoveClass(moduleClass)}
                      type="button"
                    >
                      <X aria-hidden="true" size={20} />
                    </button>
                  </>
                )}
                className={`module-class-row module-class-row-${status}`}
                key={moduleClass.id}
              >
                <span className="module-class-name">{moduleClass.name}</span>
                <span className="class-module-dates">
                  {assignment?.startDate && assignment?.endDate && `${formatDate(assignment.startDate)} - ${formatDate(assignment.endDate)}`}
                  {assignment?.startDate && !assignment?.endDate && `from ${formatDate(assignment.startDate)}`}
                  {!assignment?.startDate && assignment?.endDate && `until ${formatDate(assignment.endDate)}`}
                  {!assignment?.startDate && !assignment?.endDate && 'No time limit'}
                </span>
              </InlineListRow>
            );
          })}
        </InlineList>
      )}
      </section>
    );
  };

  return (
    <div className="module-class-groups">
      <div className="module-class-search">
        <div className="module-class-search-field">
          <Search aria-hidden="true" size={16} />
          <input
            aria-label="Search existing class"
            onChange={(event) => onClassSearchChange(event.target.value)}
            onFocus={onClassSearchFocus}
            placeholder="Search class..."
            type="search"
            value={classSearch}
          />
          {searchingClasses && <div className="class-search-spinner" aria-label="Searching classes" />}
        </div>
        {classSearchOpen && !searchingClasses && (
          <div className="module-class-search-results">
            {availableClasses.length === 0 ? (
              <span className="module-class-search-empty">No class found</span>
            ) : availableClasses.map(availableClass => (
              <button
                disabled={processingClassId === availableClass.id}
                key={availableClass.id}
                onClick={() => onAddExistingClass(availableClass)}
                type="button"
              >
                <Plus aria-hidden="true" size={15} />
                <span>{availableClass.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {renderGroup('current', grouped.current)}
      {renderGroup('future', grouped.future)}
      {renderGroup('past', grouped.past)}
    </div>
  );
};

const AdminModules: React.FC = () => {
  const [modules, setModules] = useState<AdminModulesResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [expandedModuleId, setExpandedModuleId] = useState<string | null>(null);
  const [classesByModule, setClassesByModule] = useState<Record<string, AdminClassResponse[]>>({});
  const [loadingClassesModuleId, setLoadingClassesModuleId] = useState<string | null>(null);
  const [classErrors, setClassErrors] = useState<Record<string, string | null>>({});
  const [classSearch, setClassSearch] = useState('');
  const [classSearchOpen, setClassSearchOpen] = useState(false);
  const [availableClasses, setAvailableClasses] = useState<AdminClassResponse[]>([]);
  const [searchingClasses, setSearchingClasses] = useState(false);
  const [processingClassId, setProcessingClassId] = useState<string | null>(null);
  const [editingClassModule, setEditingClassModule] = useState<{ moduleId: string; classId: string; moduleName: string; className: string } | null>(null);
  const [editingClassModuleTimeWindows, setEditingClassModuleTimeWindows] = useState<ClassTimeWindow[]>([]);
  const [isSavingClass, setIsSavingClass] = useState(false);
  const classSearchRequestRef = useRef(0);
  const navigate = useNavigate();

  const [isCreatingModule, setIsCreatingModule] = useState(false);
  const [moduleName, setModuleName] = useState('');
  const [modulePromptInfo, setModulePromptInfo] = useState('');
  const [moduleDependencyThreshold, setModuleDependencyThreshold] = useState(60);
  const [isSaving, setIsSaving] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [editingModule, setEditingModule] = useState<AdminModulesResponse | null>(null);
  const [deletingModule, setDeletingModule] = useState<AdminModulesResponse | null>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isCreatingModule) return;
    requestAnimationFrame(() => {
      nameInputRef.current?.focus();
      nameInputRef.current?.select();
    });
  }, [isCreatingModule]);

  useEffect(() => {
    fetchModules();
    fetchUserInfo();
  }, []);

  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get('create') === 'true') {
      setModuleName('');
      setModulePromptInfo('');
      setModuleDependencyThreshold(60);
      setModalError(null);
      setIsCreatingModule(true);
      url.searchParams.delete('create');
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  const fetchUserInfo = async () => {
    const currentUser = await authService.getCurrentUser();
    setUser(currentUser);
  };

  const fetchModules = async () => {
    try {
      setLoading(true);
      setError(null);
      const modulesData = await adminService.getAllModules();
      setModules(modulesData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch modules');
    } finally {
      setLoading(false);
    }
  };

  const handleViewSubjects = (moduleId: string) => {
    navigate(`/admin/subjects?moduleId=${moduleId}&from=modules`);
  };

  const toggleExpandedModule = async (moduleId: string) => {
    if (expandedModuleId === moduleId) {
      setExpandedModuleId(null);
      setClassSearch('');
      setClassSearchOpen(false);
      setAvailableClasses([]);
      return;
    }

    setExpandedModuleId(moduleId);
    setClassSearch('');
    setClassSearchOpen(false);
    setAvailableClasses([]);

    if (Object.prototype.hasOwnProperty.call(classesByModule, moduleId)) return;

    try {
      setLoadingClassesModuleId(moduleId);
      setClassErrors(currentErrors => ({ ...currentErrors, [moduleId]: null }));
      const classes = await adminService.getClassesByModule(moduleId);
      setClassesByModule(currentClasses => ({ ...currentClasses, [moduleId]: classes }));
    } catch (err) {
      setClassErrors(currentErrors => ({
        ...currentErrors,
        [moduleId]: err instanceof Error ? err.message : 'Classes could not be loaded',
      }));
    } finally {
      setLoadingClassesModuleId(currentId => currentId === moduleId ? null : currentId);
    }
  };

  const openEditClassModal = async (moduleId: string, module: AdminModulesResponse, moduleClass: AdminClassResponse) => {
    setEditingClassModule({ moduleId, classId: moduleClass.id, moduleName: module.name, className: moduleClass.name });
    const classesByModule = await adminService.getClassesByModule(moduleId);
    const classWithWindows = classesByModule.find(c => c.id === moduleClass.id);
    setEditingClassModuleTimeWindows(classWithWindows?.timeWindows ?? []);
  };

  const closeEditClassModal = () => {
    if (isSavingClass) return;
    setEditingClassModule(null);
    setEditingClassModuleTimeWindows([]);
  };

  const handleSaveClass = async (request: { moduleId: string; startDate?: string; endDate?: string; timeWindows: ClassTimeWindow[] }) => {
    if (!editingClassModule) return;

    try {
      setIsSavingClass(true);
      await adminService.updateClassModuleAssignment(editingClassModule.classId, request);
      setClassesByModule(currentClasses => ({
        ...currentClasses,
        [editingClassModule.moduleId]: (currentClasses[editingClassModule.moduleId] ?? []).map(moduleClass => {
          if (moduleClass.id !== editingClassModule.classId) return moduleClass;
          return {
            ...moduleClass,
            moduleAssignments: moduleClass.moduleAssignments.map(a =>
              a.moduleId === request.moduleId
                ? { ...a, startDate: request.startDate, endDate: request.endDate }
                : a
            ),
            timeWindows: request.timeWindows,
          };
        }),
      }));
      setEditingClassModule(null);
      setEditingClassModuleTimeWindows([]);
    } catch (err) {
      throw err;
    } finally {
      setIsSavingClass(false);
    }
  };

  const searchAvailableClasses = async (moduleId: string, search: string) => {
    const requestId = ++classSearchRequestRef.current;
    try {
      setSearchingClasses(true);
      setClassErrors(currentErrors => ({ ...currentErrors, [moduleId]: null }));
      const classes = await adminService.getAvailableClasses(moduleId, search);
      if (classSearchRequestRef.current === requestId) {
        setAvailableClasses(classes);
      }
    } catch (err) {
      if (classSearchRequestRef.current === requestId) {
        setClassErrors(currentErrors => ({
          ...currentErrors,
          [moduleId]: err instanceof Error ? err.message : 'Classes could not be searched',
        }));
      }
    } finally {
      if (classSearchRequestRef.current === requestId) {
        setSearchingClasses(false);
      }
    }
  };

  const addExistingClass = async (moduleId: string, moduleClass: AdminClassResponse) => {
    try {
      setProcessingClassId(moduleClass.id);
      setClassErrors(currentErrors => ({ ...currentErrors, [moduleId]: null }));
      await adminService.addClassToModule(moduleClass.id, moduleId);
      const startDate = new Date();
      startDate.setDate(startDate.getDate() + 1);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + 77);
      const assignedClass: AdminClassResponse = {
        ...moduleClass,
        moduleAssignments: [...moduleClass.moduleAssignments, {
          moduleId,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        }],
      };
      setClassesByModule(currentClasses => ({
        ...currentClasses,
        [moduleId]: [...(currentClasses[moduleId] ?? []), assignedClass],
      }));
      setAvailableClasses(currentClasses => currentClasses.filter(item => item.id !== moduleClass.id));
      setClassSearch('');
      setClassSearchOpen(false);
    } catch (err) {
      setClassErrors(currentErrors => ({
        ...currentErrors,
        [moduleId]: err instanceof Error ? err.message : 'Class could not be added',
      }));
    } finally {
      setProcessingClassId(currentId => currentId === moduleClass.id ? null : currentId);
    }
  };

  const removeClassFromModule = async (moduleId: string, moduleClass: AdminClassResponse) => {
    try {
      setProcessingClassId(moduleClass.id);
      setClassErrors(currentErrors => ({ ...currentErrors, [moduleId]: null }));
      await adminService.removeClassFromModule(moduleClass.id, moduleId);
      setClassesByModule(currentClasses => ({
        ...currentClasses,
        [moduleId]: (currentClasses[moduleId] ?? []).filter(item => item.id !== moduleClass.id),
      }));
    } catch (err) {
      setClassErrors(currentErrors => ({
        ...currentErrors,
        [moduleId]: err instanceof Error ? err.message : 'Class could not be removed',
      }));
    } finally {
      setProcessingClassId(currentId => currentId === moduleClass.id ? null : currentId);
    }
  };

  const handleViewUsers = (moduleClass: AdminClassResponse) => {
    const query = new URLSearchParams({ classId: moduleClass.id, className: moduleClass.name, from: 'modules' });
    navigate(`/admin/class-users?${query}`);
  };

  const handleViewDays = (moduleId: string, _moduleName: string, moduleClass: AdminClassResponse) => {
    const query = new URLSearchParams({ moduleId, classId: moduleClass.id });
    navigate(`/admin/days?${query}`);
  };

  const openCreateModuleModal = () => {
    setModalError(null);
    setModuleName('');
    setModulePromptInfo('');
    setModuleDependencyThreshold(60);
    setEditingModule(null);
    setIsCreatingModule(true);
  };

  const openEditModuleModal = (module: AdminModulesResponse) => {
    setModalError(null);
    setModuleName(module.name);
    setModulePromptInfo(module.promptInfo ?? '');
    setModuleDependencyThreshold(module.dependencyFulfillmentThreshold * 100);
    setEditingModule(module);
    setIsCreatingModule(true);
  };

  const closeCreateModuleModal = () => {
    if (isSaving) return;
    setIsCreatingModule(false);
    setEditingModule(null);
    setModalError(null);
  };

  const handleDeleteModule = async () => {
    if (!deletingModule) return;

    try {
      setIsSaving(true);
      setModalError(null);
      await adminService.deleteModule(deletingModule.id);
      setModules(currentModules => currentModules.filter(module => module.id !== deletingModule.id));
      setDeletingModule(null);
    } catch (err) {
      setModalError(err instanceof Error ? err.message : 'Module could not be deleted');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveModule = async (saveAndNew = false) => {
    try {
      setIsSaving(true);
      setModalError(null);

      if (!Number.isFinite(moduleDependencyThreshold) || moduleDependencyThreshold < 0 || moduleDependencyThreshold > 100) {
        throw new Error('The minimum score must be between 0 and 100 percent.');
      }

      if (editingModule) {
        await adminService.updateModule(editingModule.id, {
          name: moduleName.trim(),
          promptInfo: modulePromptInfo,
          dependencyFulfillmentThreshold: moduleDependencyThreshold / 100,
        });
        setModules(currentModules => currentModules.map(module =>
          module.id === editingModule.id
            ? { ...module, name: moduleName.trim(), promptInfo: modulePromptInfo, dependencyFulfillmentThreshold: moduleDependencyThreshold / 100 }
            : module
        ));
        setIsCreatingModule(false);
        setEditingModule(null);
        return;
      }

      const newModule = await adminService.createModule({
        name: moduleName.trim(),
        promptInfo: modulePromptInfo,
        dependencyFulfillmentThreshold: moduleDependencyThreshold / 100,
      });
      setModules(currentModules => [...currentModules, newModule]);

      if (saveAndNew) {
        setModuleName('');
        setModulePromptInfo('');
        setModuleDependencyThreshold(60);
        requestAnimationFrame(() => {
          nameInputRef.current?.focus();
          nameInputRef.current?.select();
        });
      } else {
        setIsCreatingModule(false);
      }
    } catch (err) {
      setModalError(err instanceof Error ? err.message : 'Module could not be saved');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="admin-modules-container">
      <AppHeader 
        user={user} 
        title="KI Expert - Admin" 
        subtitle="Manage all modules:" 
      />
      
      <main className="admin-modules-main">
        <div className="navigation-bar">
          <h2 className="navigation-bar-title">Modules</h2>
          <div className="navigation-bar-actions">
            <button className="btn btn-success" onClick={openCreateModuleModal} type="button">Add Module</button>
          </div>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading modules...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <div className="error-icon">⚠️</div>
            <h3>Error loading</h3>
            <p>{error}</p>
            <button onClick={fetchModules} className="btn btn-primary">
              Try again
            </button>
          </div>
        ) : modules.length === 0 ? (
          <div className="empty-container">
            <div className="empty-icon">📚</div>
            <h3>No modules found</h3>
            <p>There are no modules yet.</p>
          </div>
        ) : (
          <InlineList compact>
            {modules.map(module => (
              <React.Fragment key={module.id}>
                <InlineListRow
                  actions={(
                    <>
                      <button
                        aria-label="View subjects"
                        className="class-action class-action-primary"
                        data-tooltip="Subjects"
                        onClick={() => handleViewSubjects(module.id)}
                        type="button"
                      >
                        <BookOpen aria-hidden="true" size={18} />
                      </button>
                      <button
                        aria-label="Edit module"
                        className="class-action"
                        data-tooltip="Edit"
                        onClick={() => openEditModuleModal(module)}
                        type="button"
                      >
                        <Pencil aria-hidden="true" size={18} />
                      </button>
                      <button
                        aria-label="Delete module"
                        className="class-action class-action-danger"
                        data-tooltip="Delete"
                        onClick={() => setDeletingModule(module)}
                        type="button"
                      >
                        <Trash2 aria-hidden="true" size={18} />
                      </button>
                      <span aria-hidden="true" className="module-expand-indicator">
                        {expandedModuleId === module.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                      </span>
                    </>
                  )}
                  onClick={() => void toggleExpandedModule(module.id)}
                >
                  <h3 className="module-title">{module.name}</h3>
                </InlineListRow>

                {expandedModuleId === module.id && (
                  <div className="module-classes">
                    {classErrors[module.id] && (
                      <p className="class-error" role="alert">{classErrors[module.id]}</p>
                    )}
                    {loadingClassesModuleId === module.id ? (
                      <div className="class-loading">
                        <div className="loading-spinner"></div>
                        <span>Loading classes...</span>
                      </div>
                    ) : (
                    <ModuleClassesList
                      module={module}
                      moduleClasses={classesByModule[module.id] ?? []}
                      processingClassId={processingClassId}
                      availableClasses={availableClasses}
                      searchingClasses={searchingClasses}
                      classSearch={classSearch}
                      classSearchOpen={classSearchOpen}
                      onClassSearchChange={(search) => {
                        setClassSearch(search);
                        setClassSearchOpen(true);
                        void searchAvailableClasses(module.id, search);
                      }}
                      onClassSearchFocus={() => {
                        setClassSearchOpen(true);
                        void searchAvailableClasses(module.id, classSearch);
                      }}
                      onAddExistingClass={(moduleClass) => void addExistingClass(module.id, moduleClass)}
                      onEditClass={(moduleClass) => void openEditClassModal(module.id, module, moduleClass)}
                      onRemoveClass={(moduleClass) => void removeClassFromModule(module.id, moduleClass)}
                      onViewDays={handleViewDays}
                      onViewUsers={handleViewUsers}
                    />
                    )}
                  </div>
                )}
              </React.Fragment>
            ))}
          </InlineList>
        )}
      </main>

      {deletingModule && (
        <Modal
          footer={(
            <>
              <button className="btn btn-secondary" disabled={isSaving} onClick={() => setDeletingModule(null)} type="button">
                Cancel
              </button>
              <button className="btn btn-danger" disabled={isSaving} onClick={handleDeleteModule} type="button">
                {isSaving ? 'Deleting...' : 'Delete'}
              </button>
            </>
          )}
          onClose={() => !isSaving && setDeletingModule(null)}
          title="Delete module"
        >
          {modalError && <p className="modal-error">{modalError}</p>}
          <p className="modal-confirmation-message">
            Should the module “{deletingModule.name}” really be deleted?
          </p>
        </Modal>
      )}

      <ClassModuleAssignmentModal
        editing={editingClassModule ? {
          classId: editingClassModule.classId,
          moduleId: editingClassModule.moduleId,
          className: editingClassModule.className,
          moduleName: editingClassModule.moduleName,
        } : null}
        initialAssignment={editingClassModule
          ? classesByModule[editingClassModule.moduleId]?.find(c => c.id === editingClassModule.classId)?.moduleAssignments.find(a => a.moduleId === editingClassModule.moduleId)
          : undefined}
        initialTimeWindows={editingClassModuleTimeWindows}
        isSaving={isSavingClass}
        onClose={closeEditClassModal}
        onSave={handleSaveClass}
      />

      {isCreatingModule && (
        <Modal
          footer={editingModule ? (
            <>
              <button className="btn btn-secondary" disabled={isSaving} onClick={closeCreateModuleModal} type="button">
                Cancel
              </button>
              <button className="btn btn-primary" disabled={isSaving} onClick={() => handleSaveModule()} type="button">
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </>
          ) : (
            <>
              <button className="btn btn-secondary" disabled={isSaving} onClick={closeCreateModuleModal} type="button">
                Cancel
              </button>
              <button className="btn btn-secondary" disabled={isSaving} onClick={() => handleSaveModule(true)} type="button">
                {isSaving ? 'Saving...' : 'Save and New'}
              </button>
              <button className="btn btn-primary" disabled={isSaving} onClick={() => handleSaveModule()} type="button">
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </>
          )}
          onClose={closeCreateModuleModal}
          title={editingModule ? 'Edit module' : 'Add Module'}
        >
          {modalError && <p className="modal-error">{modalError}</p>}
          <div className="modal-field">
            <label htmlFor="module-name">Module name</label>
            <input
              id="module-name"
              onChange={(event) => setModuleName(event.target.value)}
              ref={nameInputRef}
              value={moduleName}
            />
          </div>
          <div className="modal-field">
            <label htmlFor="module-dependency-threshold">Minimum score to unlock the next subject (%)</label>
            <input
              id="module-dependency-threshold"
              max={100}
              min={0}
              onChange={(event) => setModuleDependencyThreshold(event.target.valueAsNumber)}
              required
              step={1}
              type="number"
              value={moduleDependencyThreshold}
            />
          </div>
          <div className="modal-field">
            <label htmlFor="module-prompt-info">Prompt Information</label>
            <textarea
              id="module-prompt-info"
              onChange={(event) => setModulePromptInfo(event.target.value)}
              placeholder="Add information about the module. this information will be added to the chat prompt"
              rows={10}
              style={{ width: '100%', minHeight: '10rem', padding: '0.625rem 0.75rem', resize: 'vertical' }}
              value={modulePromptInfo}
            />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AdminModules;
